import type { FC } from "react";
import React from "react";
import { Virtuoso } from "react-virtuoso";

import {
  EmptyView,
  ErrorView,
  LoadingView,
} from "@react/foundation/placeholder";
import { useInfiniteQuery } from "@react/foundation/query";

import type { Project } from "@apis/memory/models";

import { useQueries } from "../../../shared/hooks/useQueries.ts";

import { MemoryCard } from "./MemoryCard.tsx";

interface MemoryListProps {
  readonly projectId: Project["id"];
}

export const MemoryList: FC<MemoryListProps> = ({ projectId }) => {
  const { getMemoriesQueryOptions } = useQueries();
  const memoriesQueryOptions = getMemoriesQueryOptions({
    order: "desc",
    projectId,
  });

  const memories = useInfiniteQuery(memoriesQueryOptions.infinite);

  if (memories.status === "pending") {
    return <LoadingView />;
  }

  if (memories.status === "error") {
    return <ErrorView description="Failed to load memories." />;
  }

  if (memories.items.length === 0) {
    return (
      <EmptyView
        description="A connected agent files memories through the Memory connector."
        title="No memories yet"
      />
    );
  }

  return (
    <Virtuoso
      computeItemKey={(_, { memory }) => memory.id}
      data={memories.items}
      endReached={memories.loadNextPage}
      itemContent={(_, { memory, revision, triggers }) => (
        <MemoryCard
          className="mb-2"
          memory={memory}
          revision={revision}
          triggers={triggers}
        />
      )}
      useWindowScroll
    />
  );
};
